import { useState } from "react";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Handshake, TrendingUp, ShieldCheck } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { Reveal } from "../components/Reveal";
import { submitApplication } from "../lib/api";

const inputClass =
  "w-full rounded-xl border border-stone-300 bg-white px-4 py-3.5 font-figtree focus:ring-2 focus:ring-brand-accent focus:border-brand-accent outline-none transition-all";
const labelClass = "block text-sm font-medium text-stone-700 mb-1.5 font-figtree";

const perks = [
  [TrendingUp, "Steady flow of work", "Receive qualified, surveyed jobs in your coverage area — no chasing leads."],
  [Handshake, "Funding handled for you", "We manage grant applications and finance so you can focus on installing."],
  [ShieldCheck, "Join a trusted brand", "Work alongside accredited professionals under a recognised network."],
];

export default function JoinNetwork() {
  const [form, setForm] = useState({
    company_name: "", contact_name: "", email: "", phone: "",
    trade: "", coverage_area: "", accreditations: "", message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitApplication(form);
      setDone(true);
      toast.success("Application received — we'll be in touch soon!");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="join-network-page">
      <PageHeader
        overline="For installers"
        title="Join our installer network"
        subtitle="Are you an accredited installer of insulation, boilers, solar PV or heat pumps? Partner with us and receive qualified, fully-funded jobs."
      />

      <section className="py-16 md:py-24 bg-brand-cream">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-5 gap-10 lg:gap-16">
          <Reveal className="lg:col-span-2">
            <h2 className="font-outfit text-2xl font-semibold text-brand mb-6">Why partner with us</h2>
            <ul className="space-y-6">
              {perks.map(([Icon, t, d]) => (
                <li key={t} className="flex gap-4">
                  <span className="w-11 h-11 rounded-xl bg-white border border-stone-200 flex items-center justify-center shrink-0"><Icon className="w-5 h-5 text-brand-accent" /></span>
                  <div><p className="font-outfit font-medium text-brand text-lg">{t}</p><p className="font-figtree text-stone-600">{d}</p></div>
                </li>
              ))}
            </ul>
          </Reveal>

          <div className="lg:col-span-3">
            {done ? (
              <div data-testid="join-success" className="bg-white border border-stone-200 rounded-2xl p-10 text-center">
                <CheckCircle2 className="w-14 h-14 text-brand-success mx-auto mb-4" />
                <h3 className="font-outfit text-2xl font-semibold text-brand mb-2">Application received</h3>
                <p className="font-figtree text-stone-600">Thanks for your interest in joining the Network Survey installer network. Our partnerships team will review your details and get back to you shortly.</p>
              </div>
            ) : (
              <form data-testid="join-form" onSubmit={onSubmit} className="bg-white border border-stone-200 rounded-2xl p-6 sm:p-8 shadow-sm space-y-5">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className={labelClass}>Company name</label>
                    <input data-testid="join-company-input" required value={form.company_name} onChange={(e) => set("company_name", e.target.value)} className={inputClass} placeholder="Acme Installations Ltd" />
                  </div>
                  <div>
                    <label className={labelClass}>Contact name</label>
                    <input data-testid="join-contact-input" required value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} className={inputClass} placeholder="John Doe" />
                  </div>
                  <div>
                    <label className={labelClass}>Email</label>
                    <input data-testid="join-email-input" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} className={inputClass} placeholder="john@acme.co.uk" />
                  </div>
                  <div>
                    <label className={labelClass}>Phone</label>
                    <input data-testid="join-phone-input" required value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inputClass} placeholder="07123 456789" />
                  </div>
                  <div>
                    <label className={labelClass}>Primary trade</label>
                    <select data-testid="join-trade-select" required value={form.trade} onChange={(e) => set("trade", e.target.value)} className={inputClass}>
                      <option value="">Select…</option>
                      <option>Insulation</option>
                      <option>Boilers / Heating</option>
                      <option>Solar PV</option>
                      <option>Heat Pumps</option>
                      <option>Multiple</option>
                    </select>
                  </div>
                  <div>
                    <label className={labelClass}>Coverage area</label>
                    <input data-testid="join-coverage-input" required value={form.coverage_area} onChange={(e) => set("coverage_area", e.target.value)} className={inputClass} placeholder="e.g. North West England" />
                  </div>
                </div>
                <div>
                  <label className={labelClass}>Accreditations (MCS, Gas Safe, TrustMark…)</label>
                  <input data-testid="join-accreditations-input" value={form.accreditations} onChange={(e) => set("accreditations", e.target.value)} className={inputClass} placeholder="List your accreditations" />
                </div>
                <div>
                  <label className={labelClass}>Tell us about your business (optional)</label>
                  <textarea data-testid="join-message-input" value={form.message} onChange={(e) => set("message", e.target.value)} rows={3} className={inputClass} placeholder="Team size, experience, capacity…" />
                </div>
                <button data-testid="join-submit-button" type="submit" disabled={loading} className="w-full bg-brand-accent text-white hover:bg-brand-accenthover transition-colors rounded-full px-8 py-4 font-medium inline-flex items-center justify-center gap-2 disabled:opacity-70">
                  {loading ? <><Loader2 className="w-5 h-5 animate-spin" /> Submitting…</> : "Apply to join the network"}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
